import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSocratesV2InterviewListQuery } from "./useSocratesV2InterviewListQuery";
import {
  useUpdateGroupMutation,
  useDeleteGroupMutation,
} from "../../tutorV2/hooks/queries/mutations/useGroupMutations";
import { useAddToStorageMutation } from "../../tutorV2/hooks/queries/mutations/useStorageMutations";
import { useGroupListStore } from "../../tutorV2/store";
import { Mode } from "../../tutorV2/types";

/**
 * useSocratesV2GroupMenuListView
 *
 * Socrates V2 전용 그룹 목록 presentation 훅
 * 새로운 RESTful API (socratesApi)를 사용
 *
 * 기능:
 * - 면접 목록 무한 스크롤 데이터 페칭 (새 API)
 * - 날짜별 섹션 그룹핑
 * - 아이템 클릭/편집/삭제/보관 핸들러
 */

interface GroupedItem {
  id: string;
  label: string;
  date?: string;
  showDate?: boolean;
  isStorage: false;
  isCompleted?: boolean;
}

export const useSocratesV2GroupMenuListView = () => {
  const navigate = useNavigate();
  const { setCurrentGroupId } = useGroupListStore();

  // Socrates V2 전용 Infinite Query (새 API 사용)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useSocratesV2InterviewListQuery();

  // Mutations (기존 것 재사용 - 필요시 V2 전용으로 분리 가능)
  const updateGroupMutation = useUpdateGroupMutation(Mode.SOCRATES);
  const deleteGroupMutation = useDeleteGroupMutation(Mode.SOCRATES);
  const addToStorageMutation = useAddToStorageMutation(Mode.SOCRATES);

  // 편집 상태
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  /**
   * 날짜별 그룹핑 및 items 변환
   */
  const groupedItems = useMemo(() => {
    if (!data) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const allInterviews = data.pages
      .flatMap((page) => page.interviews)
      .reverse();
    const grouped: Record<string, GroupedItem[]> = {};

    allInterviews.forEach((item) => {
      // id가 없는 항목은 건너뜀
      if (!item.id) return;

      const itemDate = new Date(item.updated_at);
      const itemDateOnly = new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate()
      );

      let section: string;

      if (itemDateOnly.getTime() === today.getTime()) {
        section = "오늘";
      } else if (itemDateOnly >= sevenDaysAgo) {
        section = "지난 7일";
      } else {
        const year = itemDate.getFullYear();
        const month = itemDate.getMonth() + 1;
        section = `${year}년 ${month}월`;
      }

      if (!grouped[section]) {
        grouped[section] = [];
      }

      grouped[section].push({
        id: String(item.id),
        label: item.title || "제목 없음",
        date: section,
        showDate: false,
        isStorage: false,
        isCompleted: item.is_completed,
      });
    });

    // 각 섹션의 첫 항목에만 날짜 표시
    const result: GroupedItem[] = [];
    Object.entries(grouped).forEach(([_, items]) => {
      items.forEach((item, index) => {
        result.push({
          ...item,
          showDate: index === 0,
        });
      });
    });

    return result;
  }, [data]);

  /**
   * 아이템 클릭 - 해당 면접으로 네비게이션
   */
  const handleItemClick = useCallback(
    (itemId: string) => {
      const navState = {
        from_chat: "group" as const,
        his_cnt: 0,
        is_writed: false,
        is_completed: false,
      };

      setCurrentGroupId(itemId);
      navigate(`/socrates/${itemId}`, { state: navState });
    },
    [navigate, setCurrentGroupId]
  );

  /**
   * 편집 모드 진입
   */
  const handleItemEdit = useCallback((itemId: string) => {
    setEditingItemId(itemId);
  }, []);

  /**
   * 면접 삭제
   */
  const handleItemDelete = useCallback(
    async (itemId: string) => {
      await deleteGroupMutation.mutateAsync(itemId);
    },
    [deleteGroupMutation]
  );

  /**
   * 보관함에 저장
   */
  const handleItemSave = useCallback(
    async (itemId: string) => {
      const item = groupedItems.find((i) => i.id === itemId);
      if (!item) return;

      const result = await addToStorageMutation.mutateAsync({
        groupId: itemId,
        title: item.label,
        isCompleted: item.isCompleted,
      });

      if (!result.success && result.message) {
        console.warn(result.message);
      }
    },
    [addToStorageMutation, groupedItems]
  );

  /**
   * 제목 변경 완료
   */
  const handleItemTitleChange = useCallback(
    async (itemId: string, newTitle: string) => {
      await updateGroupMutation.mutateAsync({
        groupId: itemId,
        title: newTitle,
      });

      setEditingItemId(null);
    },
    [updateGroupMutation]
  );

  /**
   * 무한 스크롤 핸들러
   */
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    // 데이터
    items: groupedItems,
    label: "소크라테스 목록",

    // 상태
    editingItemId,
    isLoading:
      isLoading ||
      updateGroupMutation.isPending ||
      deleteGroupMutation.isPending ||
      addToStorageMutation.isPending,
    hasMore: hasNextPage,
    isFetchingMore: isFetchingNextPage,

    // 핸들러
    handleItemClick,
    handleItemEdit,
    handleItemDelete,
    handleItemSave,
    handleItemTitleChange,
    handleLoadMore,
  };
};
